import React, { useMemo, useState } from 'react';
import {
  ArrowDownWideNarrow,
  ChevronRight,
  Clock,
  ExternalLink,
  Filter,
  Globe2,
  MapPin,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import { CLASSIFICATIONS, DISEASES } from '../mock';

const SORTS = {
  newest: 'Newest',
  severity: 'Priority',
  country: 'Country',
  disease: 'Disease',
};

const TABS = {
  signals: 'Signals',
  countries: 'Countries',
  sources: 'Sources',
};

const priority = { official: 3, emerging: 2, chatter: 1 };

const getAge = (signal) => Number.isFinite(Number(signal.hoursAgo)) ? Number(signal.hoursAgo) : 999999;

const NewsFeed = ({
  signals,
  activeDiseases,
  query,
  setQuery,
  sourceFilter,
  setSourceFilter,
  countryFocus,
  setCountryFocus,
  onSelectSignal,
  lastUpdatedLabel,
  dashboard,
  sourceOptions,
  countryOptions,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [tab, setTab] = useState('signals');
  const diseaseMap = useMemo(() => Object.fromEntries(DISEASES.map(d => [d.id, d])), []);

  const sortedSignals = useMemo(() => {
    return [...signals].sort((a, b) => {
      if (sortBy === 'severity') return (priority[b.classification] - priority[a.classification]) || (getAge(a) - getAge(b));
      if (sortBy === 'country') return `${a.country || ''}${a.place || ''}`.localeCompare(`${b.country || ''}${b.place || ''}`);
      if (sortBy === 'disease') return `${diseaseMap[a.diseaseId]?.name || a.diseaseId}`.localeCompare(`${diseaseMap[b.diseaseId]?.name || b.diseaseId}`);
      return getAge(a) - getAge(b);
    });
  }, [diseaseMap, signals, sortBy]);

  const pinnedSignals = useMemo(() => sortedSignals.slice(0, 4), [sortedSignals]);

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="absolute right-4 top-[64px] z-20 rounded-l-lg border border-cyan-900/40 bg-[#08191d]/95 px-3 py-2 text-[12px] text-cyan-100 hover:bg-cyan-500/10"
        aria-label="Open signal console"
      >
        <ChevronRight className="h-4 w-4 rotate-180" />
      </button>
    );
  }

  return (
    <aside
      className="outbreak-feed fade-in absolute bottom-4 right-3 top-[64px] z-20 flex w-[430px] flex-col rounded-lg border border-cyan-900/40 bg-[#08191d]/95 backdrop-blur-md"
      style={{ boxShadow: '0 12px 36px rgba(0,0,0,0.55)' }}
    >
      <div className="border-b border-cyan-900/40 px-4 pb-3 pt-3.5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-cyan-50">Signal console</span>
              <span className="text-[11px] text-cyan-700">·</span>
              <span className="text-[11px] font-medium text-cyan-400">{signals.length}</span>
            </div>
            <div className="mt-0.5 text-[10.5px] text-cyan-700">Search, inspect, and focus the public snapshot</div>
          </div>
          <button onClick={() => setCollapsed(true)} className="p-1 text-cyan-500 hover:text-cyan-200" aria-label="Collapse signal console">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-1.5">
          {Object.values(CLASSIFICATIONS).map(c => (
            <div key={c.id} className="rounded-md border border-cyan-900/40 bg-cyan-500/5 px-2 py-1.5">
              <div className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: c.color }} />
                <span className="truncate text-[10px] text-cyan-300">{c.label}</span>
              </div>
              <div className="mt-0.5 text-[15px] font-semibold leading-none text-cyan-50">{dashboard.classCounts[c.id] || 0}</div>
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-center gap-2 rounded-md border border-cyan-900/40 bg-[#061417]/70 px-2.5 py-2">
          <Search className="h-3.5 w-3.5 text-cyan-600" />
          <input
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search disease, country, source..."
            className="min-w-0 flex-1 bg-transparent text-[12px] text-cyan-50 outline-none placeholder:text-cyan-700"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-cyan-600 hover:text-cyan-200" aria-label="Clear search">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="mt-2 flex items-center gap-1.5 overflow-x-auto feed-scroll">
          {Object.entries(TABS).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`shrink-0 rounded-md px-2.5 py-1 text-[10.5px] transition-colors ${
                tab === id ? 'bg-cyan-400/15 text-cyan-100' : 'text-cyan-500 hover:bg-cyan-500/10 hover:text-cyan-200'
              }`}
            >
              {label}
            </button>
          ))}
          <span className="ml-1 flex shrink-0 items-center gap-1 text-[10.5px] text-cyan-600">
            <ArrowDownWideNarrow className="h-3 w-3" /> Sort
          </span>
          {Object.entries(SORTS).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setSortBy(id)}
              className={`shrink-0 rounded-md px-2 py-1 text-[10.5px] transition-colors ${
                sortBy === id ? 'bg-cyan-400/15 text-cyan-100' : 'text-cyan-500 hover:bg-cyan-500/10 hover:text-cyan-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-2 flex gap-1.5 overflow-x-auto feed-scroll">
          {sourceFilter !== 'all' && (
            <button onClick={() => setSourceFilter('all')} className="flex shrink-0 items-center gap-1 rounded-md border border-cyan-900/50 px-2 py-1 text-[10.5px] text-cyan-300 hover:border-cyan-700 hover:text-cyan-100">
              Source: {sourceFilter} <X className="h-3 w-3" />
            </button>
          )}
          {countryFocus !== 'all' && (
            <button onClick={() => setCountryFocus('all')} className="flex shrink-0 items-center gap-1 rounded-md border border-cyan-900/50 px-2 py-1 text-[10.5px] text-cyan-300 hover:border-cyan-700 hover:text-cyan-100">
              Country: {countryFocus} <X className="h-3 w-3" />
            </button>
          )}
          {query && (
            <button onClick={() => setQuery('')} className="flex shrink-0 items-center gap-1 rounded-md border border-cyan-900/50 px-2 py-1 text-[10.5px] text-cyan-300 hover:border-cyan-700 hover:text-cyan-100">
              Search <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 feed-scroll">
        {tab === 'signals' && (
          <>
            {pinnedSignals.length > 0 && (
              <div className="mb-2 rounded-lg border border-cyan-900/35 bg-cyan-400/[0.035] p-2">
                <div className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-cyan-600">
                  <ShieldCheck className="h-3 w-3" /> Priority signals
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {pinnedSignals.map(signal => (
                    <button key={signal.id} onClick={() => onSelectSignal(signal)} className="rounded-md border border-cyan-900/35 bg-[#061417]/70 px-2 py-1.5 text-left text-[10.5px] leading-snug text-cyan-100 hover:border-cyan-700/60 hover:bg-cyan-500/[0.05]">
                      <span className="line-clamp-2">{signal.headline}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {sortedSignals.map(signal => {
              const c = CLASSIFICATIONS[signal.classification];
              const d = diseaseMap[signal.diseaseId];
              return (
                <button
                  key={signal.id}
                  onClick={() => onSelectSignal(signal)}
                  className="group mb-2 w-full rounded-lg border border-cyan-900/35 bg-[#07171b]/82 p-3 text-left transition-colors hover:border-cyan-700/60 hover:bg-cyan-500/[0.05]"
                >
                  <div className="mb-2 flex items-center gap-2">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: c.color, boxShadow: `0 0 6px ${c.color}` }} />
                    <span className="text-[9.5px] uppercase tracking-[0.14em] text-cyan-500">{c.label}</span>
                    <span className="text-cyan-900">·</span>
                    <span className="truncate text-[10.5px] font-medium" style={{ color: d?.color }}>{d?.name}</span>
                    <span className="ml-auto flex shrink-0 items-center gap-1 text-[10.5px] text-cyan-700">
                      <Clock className="h-2.5 w-2.5" /> {getAge(signal) > 0 ? `${getAge(signal)}h` : 'now'}
                    </span>
                  </div>
                  <div className="mb-2 text-[12.8px] font-medium leading-snug text-cyan-50 group-hover:text-white">
                    {signal.headline}
                  </div>
                  <div className="grid grid-cols-[1fr_auto] items-end gap-3 text-[10.5px] text-cyan-600">
                    <div className="min-w-0">
                      <div className="truncate text-cyan-300/80">{signal.place}{signal.country && signal.place !== signal.country ? `, ${signal.country}` : ''}</div>
                      <div className="mt-0.5 truncate">Source: {signal.source}</div>
                    </div>
                    {signal.sourceUrl ? (
                      <a
                        href={signal.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        className="flex items-center gap-1 rounded-md border border-cyan-900/50 px-2 py-1 text-cyan-300 transition-colors hover:border-cyan-600 hover:text-cyan-100"
                      >
                        Open <ExternalLink className="h-2.5 w-2.5 opacity-70" />
                      </a>
                    ) : (
                      <span className="rounded-md border border-cyan-900/40 px-2 py-1 text-cyan-700">No link</span>
                    )}
                  </div>
                </button>
              );
            })}
          </>
        )}

        {tab === 'countries' && (
          <div className="space-y-2">
            {dashboard.countryCounts.map(([country, count]) => (
              <button
                key={country}
                onClick={() => setCountryFocus(country)}
                className="w-full rounded-lg border border-cyan-900/35 bg-[#07171b]/82 p-3 text-left transition-colors hover:border-cyan-700/60 hover:bg-cyan-500/[0.05]"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-2 font-medium text-cyan-50">
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-cyan-500" />
                    <span className="truncate">{country}</span>
                  </span>
                  <span className="shrink-0 text-[12px] text-cyan-400">{count} signals</span>
                </div>
                <div className="mt-2 h-1 overflow-hidden rounded-full bg-cyan-950/80">
                  <div className="h-full rounded-full bg-emerald-400" style={{ width: `${Math.max(8, (count / Math.max(1, dashboard.countryCounts[0]?.[1] || count)) * 100)}%` }} />
                </div>
              </button>
            ))}
          </div>
        )}

        {tab === 'sources' && (
          <div className="space-y-2">
            {dashboard.sourceCounts.map(([source, count]) => (
              <button
                key={source}
                onClick={() => setSourceFilter(source)}
                className="w-full rounded-lg border border-cyan-900/35 bg-[#07171b]/82 p-3 text-left transition-colors hover:border-cyan-700/60 hover:bg-cyan-500/[0.05]"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-2 font-medium text-cyan-50">
                    <Globe2 className="h-3.5 w-3.5 shrink-0 text-cyan-500" />
                    <span className="truncate">{source}</span>
                  </span>
                  <span className="shrink-0 text-[12px] text-cyan-400">{count} signals</span>
                </div>
                <div className="mt-2 h-1 overflow-hidden rounded-full bg-cyan-950/80">
                  <div className="h-full rounded-full bg-amber-300" style={{ width: `${Math.max(8, (count / Math.max(1, dashboard.sourceCounts[0]?.[1] || count)) * 100)}%` }} />
                </div>
              </button>
            ))}
            {sourceOptions.length > 1 && (
              <div className="rounded-lg border border-cyan-900/35 bg-[#061417]/70 p-3">
                <div className="mb-2 text-[10px] uppercase tracking-[0.14em] text-cyan-600">Available sources</div>
                <div className="flex flex-wrap gap-1.5">
                  {sourceOptions.filter(source => source !== 'all').map(source => (
                    <button key={source} onClick={() => setSourceFilter(source)} className="rounded-md border border-cyan-900/40 px-2 py-1 text-[10.5px] text-cyan-300 hover:border-cyan-700 hover:text-cyan-100">
                      {source}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {signals.length === 0 && (
          <div className="px-4 py-10 text-center text-[12px] text-cyan-700">
            No signals match your filters.
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-cyan-900/40 px-4 py-2.5 text-[10.5px] text-cyan-700">
        <span>Updated {lastUpdatedLabel}</span>
        <span className="flex items-center gap-1 text-cyan-500">
          <Filter className="h-3 w-3" /> {activeDiseases.length} diseases active · {countryOptions.length - 1} countries
        </span>
      </div>
    </aside>
  );
};

export default NewsFeed;
